// eslint-disable-file @typescript-eslint/no-namespace

import { CONFERENCES_HISTORY_BATCH_SIZE } from '../../../../../../common/const/conferences-history';
import { BroadcastInfo, BroadcastState } from '../../../../pages/broadcast-viewer/types';
import { GRID_VIEW } from '../../../../pages/call/constants';
import { ArtifactType } from '../../../../pages/conferences-history/features/sidebar/slice';
import { ChatData, EmptyObject } from '../../../../types';
import { Role } from '../../permissions';
import { SessionState } from '../../session-state';
import {
    FullStatsReport,
    StatsEventReviewShow,
    StatsEventReviewSubmit,
    StatsEventSupportFromConferenceShow,
    StatsEventSupportFromConferenceSubmit,
    StatsEventSupportShow,
} from '../../stats';
import { getUrlForLeaveRoom } from './helpers';
import { CloudApiTransport } from './transport';
import { CloudAPI } from './types';

export class CloudApi {
    private transport: CloudApiTransport;

    constructor(transport: CloudApiTransport) {
        this.transport = transport;
    }

    public setHeader(key: string, value: string) {
        this.transport.setHeader(key, value);
    }

    public setApiAuthorizationHeader(token: string) {
        this.transport.setApiAuthorizationHeader(token);
    }

    public disableCredentials() {
        this.transport.disableCredentials();
    }

    public createConference(
        payload: CloudAPI.CreateConference.Request,
        enableGoloom: boolean,
        appVersion: string,
        isGoloomRc?: boolean
    ): Promise<CloudAPI.CreateConference.Response> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences',
            payload,
            query: {
                next_gen_media_platform_allowed: String(enableGoloom),
                is_rc: isGoloomRc ? String(isGoloomRc) : undefined,
            },
            additionalHeaders: {
                'X-Telemost-Client-Version': appVersion,
            },
        });
    }

    public connectToConference({
        roomId,
        telemostHost,
        searchString,
        displayName,
        enableGoloom,
        isGoloomRc,
        additionalHeaders = {},
        appVersion,
    }: CloudAPI.ConnectToConference.Request): Promise<CloudAPI.ConnectToConference.Response> {
        const uri = new URL(`/j/${roomId}${searchString || ''}`, `https://${telemostHost}`);
        uri.searchParams.delete('constraints');

        return this.transport.get({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/connection',
            params: {
                conferenceUrl: uri.toString(),
            },
            timeout: 10000,
            query: {
                next_gen_media_platform_allowed: String(enableGoloom),
                display_name: displayName,
                waiting_room_supported: 'true',
                is_rc: isGoloomRc ? String(isGoloomRc) : undefined,
            },
            additionalHeaders: {
                'X-Telemost-Client-Version': appVersion,
                ...additionalHeaders,
            },
        });
    }

    public fetchParticipantsInfos(
        confUri: string,
        peerIds: string[]
    ): Promise<CloudAPI.FetchParticipantsInfos.Response> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/peers',
            params: {
                conferenceUrl: confUri,
            },
            payload: {
                peers_ids: peerIds,
            },
        });
    }

    public leaveRoomByBeacon(roomId: string, leaveRoomOptions: CloudAPI.LeaveRoomOptions): void {
        navigator.sendBeacon?.(getUrlForLeaveRoom(roomId, leaveRoomOptions));
    }

    public leaveRoomByFetch(roomId: string, leaveRoomOptions: CloudAPI.LeaveRoomOptions): Promise<void> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/rooms/:roomId/leave',
            params: {
                roomId,
            },
            query: {
                peer_id: leaveRoomOptions.peerId,
                peer_token: leaveRoomOptions.peerToken,
                media_session_id: leaveRoomOptions.mediaSessionId,
            },
            keepalive: true,
        });
    }

    public createBroadcast(
        confUri: string,
        broadcastData: CloudAPI.CreateBroadcast.Request
    ): Promise<CloudAPI.CreateBroadcast.Response> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/broadcast',
            params: {
                conferenceUrl: confUri,
            },
            payload: broadcastData,
        });
    }

    public startBroadcast(
        confUri: string,
        broadcastUri: string,
        view: GRID_VIEW
    ): Promise<CloudAPI.StartBroadcast.Response> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/broadcast/:broadcastUrl/start',
            params: {
                conferenceUrl: confUri,
                broadcastUrl: broadcastUri,
            },
            query: {
                view,
            },
        });
    }

    public stopBroadcast(confUri: string, broadcastUri: string): Promise<CloudAPI.StopBroadcast.Response> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/broadcast/:broadcastUrl/stop',
            params: {
                conferenceUrl: confUri,
                broadcastUrl: broadcastUri,
            },
        });
    }

    public endBroadcast(confUri: string, broadcastUri: string) {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/broadcast/:broadcastUrl/end',
            params: {
                conferenceUrl: confUri,
                broadcastUrl: broadcastUri,
            },
            retryOptions: {
                retryCount: 3,
                retryDelay: 500,
            },
        });
    }

    public setViewInBroadcast(
        confUri: string,
        broadcastUri: string,
        view: GRID_VIEW
    ): Promise<CloudAPI.SetViewInBroadcast.Response> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/broadcast/:broadcastUrl/set_view',
            params: {
                conferenceUrl: confUri,
                broadcastUrl: broadcastUri,
            },
            query: {
                view,
            },
        });
    }

    public getBroadcastState(broadcastUri: string): Promise<BroadcastState> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/broadcast/:broadcastUrl/state',
            params: {
                broadcastUrl: broadcastUri,
            },
        });
    }

    public logStats(confId: string, endPointId: string, statsReport: FullStatsReport): Promise<EmptyObject | void> {
        return this.transport.post<EmptyObject>({
            path: '/telemost_front/v1/telemost/stat/log',
            retryOptions: {
                retryCount: 3,
            },
            query: {
                conf_id: confId,
                endpoint_id: endPointId,
            },
            payload: {
                stats_report: statsReport,
            },
        });
    }

    public getChatHistory(roomId: string, userId: string): Promise<CloudAPI.GetChatHistory.Response> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/chats/:roomId/history',
            params: {
                roomId,
            },
            query: {
                user: userId,
            },
        });
    }

    public getMeUser(): Promise<CloudAPI.GetMeUser.Response> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/users/me',
            retryOptions: {
                retryCount: 3,
                retryDelay: 1000,
            },
        });
    }

    public connectToBroadcast(liveUrl: string): Promise<BroadcastInfo> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/broadcast/:liveUrl/connection',
            params: {
                liveUrl,
            },
        });
    }

    public joinToChat(roomId: string, userId?: string): Promise<ChatData> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/chats/:roomId/join',
            params: {
                roomId,
            },
            query: {
                user: userId,
            },
            retryOptions: {
                retryCount: 3,
                retryDelay: 2000,
            },
        });
    }

    public createChat(roomId: string, userId?: string): Promise<ChatData> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/chats/:roomId',
            params: {
                roomId,
            },
            query: {
                user: userId,
            },
        });
    }

    public feedbackWasShown(
        payload: CloudAPI.FeedbackWasShown.Request
    ): Promise<CloudAPI.FeedbackWasShown.Response | void> {
        return this.transport.post<EmptyObject>({
            path: '/telemost_front/v1/telemost/stat/log',
            retryOptions: {
                retryCount: 3,
            },
            payload: {
                stats_report: {
                    event_type: 'user_review_show',

                    ...payload,
                } as StatsEventReviewShow,
            },
        });
    }

    public sendFeedback(payload: CloudAPI.SendFeedback.Request): Promise<CloudAPI.SendFeedback.Response | void> {
        return this.transport.post<EmptyObject>({
            path: '/telemost_front/v1/telemost/stat/log',
            retryOptions: {
                retryCount: 3,
            },
            payload: {
                stats_report: {
                    event_type: 'user_review_submit',

                    ...payload,
                } as StatsEventReviewSubmit,
            },
        });
    }

    public supportFormWasShown(
        payload: CloudAPI.SupportFormWasShown.Request
    ): Promise<CloudAPI.SupportFormWasShown.Response | void> {
        return this.transport.post<EmptyObject>({
            path: '/telemost_front/v1/telemost/stat/log',
            retryOptions: {
                retryCount: 3,
            },
            payload: {
                stats_report: {
                    event_type: 'user_support_show',

                    ...payload,
                } as StatsEventSupportShow,
            },
        });
    }

    public reportAProblemFormWasShown(
        payload: CloudAPI.ReportAProblemFormWasShown.Request
    ): Promise<CloudAPI.ReportAProblemFormWasShown.Response | void> {
        return this.transport.post<EmptyObject>({
            path: '/telemost_front/v1/telemost/stat/log',
            retryOptions: {
                retryCount: 3,
            },
            payload: {
                stats_report: {
                    event_type: 'user_support_from_conference_show',

                    ...payload,
                } as StatsEventSupportFromConferenceShow,
            },
        });
    }

    public sendReportAProblem(
        payload: CloudAPI.SendReportAProblem.Request
    ): Promise<CloudAPI.SendReportAProblem.Response | void> {
        return this.transport.post<EmptyObject>({
            path: '/telemost_front/v1/telemost/stat/log',
            retryOptions: {
                retryCount: 3,
            },
            payload: {
                stats_report: {
                    event_type: 'user_support_from_conference_submit',

                    ...payload,
                } as StatsEventSupportFromConferenceSubmit,
            },
        });
    }

    public sendCommand(uri: string, payload: CloudAPI.SendCommand.Request): Promise<EmptyObject> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/commands/:command',
            params: {
                conferenceUrl: uri,
                command: payload.command,
            },
            query: {
                peer_id: payload.peerId,
                exclude_peer_id: payload.excludePeerId,
                target_uid: payload.targetUid,
                role: payload.role,
                with_ban: String(payload.withBan),
                allow: String(payload.allow),
                mail_recipient: payload.mailRecipient,
                without_removal: String(payload.withoutRemoval),
            },
        });
    }

    public setSummarizationSettings(
        uri: string,
        payload: CloudAPI.SetSummarizationSettings.Request
    ): Promise<CloudAPI.SetSummarizationSettings.Response> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/summarization/settings',
            params: {
                conferenceUrl: uri,
            },
            payload,
        });
    }

    public setConferenceAccessLevel(uri: string, accessLevel: string): Promise<EmptyObject> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/set-access',
            params: {
                conferenceUrl: uri,
            },
            payload: {
                access_level: accessLevel,
            },
        });
    }

    public setBroadcastAccessLevel(uri: string, broadcastUri: string, accessLevel: string): Promise<EmptyObject> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/broadcast/:broadcastUrl/set-access',
            params: {
                conferenceUrl: uri,
                broadcastUrl: broadcastUri,
            },
            payload: {
                access_level: accessLevel,
            },
        });
    }

    public refreshOffer({
        roomId,
        peerId,
        peerToken,
        mediaSessionId,
    }: CloudAPI.RefreshOffer.Request): Promise<EmptyObject> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/rooms/:roomId/peers/:peerId/refresh_offer',
            params: {
                roomId,
                peerId,
            },
            query: {
                peer_token: peerToken,
                media_session_id: mediaSessionId,
            },
            retryOptions: {
                retryCount: 3,
            },
        });
    }

    public getPeersState(
        uri: string,
        payload: CloudAPI.GetPeersState.Request
    ): Promise<CloudAPI.GetPeersState.Response> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/request-states',
            params: { conferenceUrl: uri },
            payload,
        });
    }

    public setPeersState(
        uri: string,
        peerId: string,
        payload: CloudAPI.SetPeerState.Request
    ): Promise<CloudAPI.SetPeerState.Response> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/peers/:peerId/state',
            params: {
                conferenceUrl: uri,
                peerId,
            },
            payload,
            query: {
                clientInstanceId: SessionState.instanceId,
            },
        });
    }

    public setPeerReaction(
        uri: string,
        peerId: string,
        payload: CloudAPI.SetPeerReaction.Request
    ): Promise<CloudAPI.SetPeerReaction.Response> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/peers/:peerId/state/reaction',
            params: {
                conferenceUrl: uri,
                peerId,
            },
            payload,
        });
    }

    public setPeerEmotion(
        uri: string,
        peerId: string,
        payload: CloudAPI.SetPeerEmotion.Request
    ): Promise<CloudAPI.SetPeerEmotion.Response> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/peers/:peerId/emotion',
            params: {
                conferenceUrl: uri,
                peerId,
            },
            payload,
        });
    }

    public sendFeedbackToSupport(
        query: CloudAPI.SendFeedbackToSupport.Query,
        payload: CloudAPI.SendFeedbackToSupport.Request
    ): Promise<void> {
        return this.transport.put({
            path: '/telemost_front/v1/telemost/clients/fos',
            payload,
            query: {
                ...query,
            },
            withIdempotencyHeader: false,
        });
    }

    public waitingRoomCheckAccess(uri: string): Promise<CloudAPI.WaitingRoomCheckAccess.Response> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/waiting-rooms/check-access',
            timeout: 10000,
            params: {
                conferenceUrl: uri,
            },
        });
    }

    public getWaitingRoomPeers(uri: string): Promise<CloudAPI.GetWaitingRoomPeers.Response> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/waiting-rooms/peers',
            timeout: 10000,
            params: {
                conferenceUrl: uri,
            },
        });
    }

    public waitingRoomAdmit(uri: string, peerId: string): Promise<void> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/commands/admit',
            params: {
                conferenceUrl: uri,
            },
            query: {
                peer_id: peerId,
            },
        });
    }

    public waitingRoomAdmitAll(uri: string): Promise<void> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/commands/admit-all',
            params: {
                conferenceUrl: uri,
            },
        });
    }

    public leaveWaitingRoom(uri: string): Promise<void> {
        return this.transport.put({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/waiting-rooms/leave',
            params: {
                conferenceUrl: uri,
            },
            keepalive: true,
        });
    }

    public getMatrix(uri: string): Promise<CloudAPI.GetMatrix.Response> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/permissions-matrix',
            params: {
                conferenceUrl: uri,
            },
            retryOptions: {
                retryCount: 3,
                retryDelay: 5_000,
            },
        });
    }

    public setMatrix(uri: string, payload: CloudAPI.SetMatrix.Request): Promise<void> {
        return this.transport.post({
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/permissions-matrix',
            params: {
                conferenceUrl: uri,
            },
            payload,
        });
    }

    public resetAllRolePermissions(uri: string, role?: Role): Promise<void> {
        const query = role ? { role } : undefined;

        return this.transport.post({
            // eslint-disable-next-line max-len
            path: '/telemost_front/v2/telemost/conferences/:conferenceUrl/permissions-matrix/reset-all-role-permissions',
            params: {
                conferenceUrl: uri,
            },
            query,
        });
    }

    public getConferencesHistoryBatch(
        payload: CloudAPI.ConferencesHistory.Request,
        iterationKey?: string
    ): Promise<CloudAPI.ConferencesHistory.Response> {
        const query: Record<string, string> = { limit: CONFERENCES_HISTORY_BATCH_SIZE.toString() };
        if (iterationKey) {
            query.iteration_key = iterationKey;
        }
        return this.transport.post({
            path: '/telemost_front/v2/telemost/history/conferences/search',
            query,
            payload,
        });
    }

    public getConferencesArtifacts(
        conferenceId: string,
        sessionId: string
    ): Promise<CloudAPI.ConferenceArtifacts.Response> {
        return this.transport.get({
            path: '/telemost_front/v2/telemost/history/conferences/:conferenceId/sessions/:sessionId/artifacts',
            params: {
                conferenceId,
                sessionId,
            },
        });
    }

    public getArtifactRecipients(
        confShortUrlId: string,
        sessionId: string,
        artifactId: string,
        artifactType: ArtifactType,
        offset: number,
        limit: number
    ): Promise<CloudAPI.ConferenceArtifactsRecipients.Response> {
        const PATH_BASE =
            '/telemost_front/v2/telemost/history/conferences/:confShortUrlId/sessions/:sessionId/artifacts';
        const path =
            artifactType === 'localRecordings'
                ? `${PATH_BASE}/local-recordings/recipients`
                : `${PATH_BASE}/:artifactType/:artifactId/recipients`;
        return this.transport.get({
            path,
            params: {
                confShortUrlId,
                sessionId,
                artifactId,
                artifactType,
            },
            query: { offset: offset.toString(), limit: limit.toString() },
        });
    }

    public getSessionReport(confShortUrlId: string, sessionId: string): Promise<Blob> {
        return this.transport.get({
            // eslint-disable-next-line max-len
            path: '/telemost_front/v2/telemost/history/conferences/:confShortUrlId/sessions/:sessionId/artifacts/meeting-report',
            params: {
                confShortUrlId,
                sessionId,
            },
            bodyParseMethod: 'blob',
            additionalHeaders: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        });
    }
}
